import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { invalidateCampaignQueries } from '#/entities/campaign'
import {
  useGetCampaignApiV1CampaignsCampaignIdGet,
  useUpdateCampaignApiV1CampaignsCampaignIdPatch,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

/**
 * The windows the menu offers. The service accepts 1–90 days; these are the
 * ones worth a click, and the current value is always shown even when it is
 * not one of them (a campaign created through the API can carry any number).
 */
export const ATTRIBUTION_WINDOWS = [1, 3, 7, 14, 30, 60, 90] as const

export interface CampaignAttributionResult {
  /** The saved window, or null until the read lands. Drives the tick. */
  current: number | null
  /** Every option to draw, the saved one merged in and sorted. */
  options: number[]
  onChange: (days: number) => void
  isSaving: boolean
}

/**
 * The Performance card's "· change": how many days after a send a booked order
 * still counts as this campaign's.
 *
 * The figure itself is not on `/overview` — that sends `performance_basis` as a
 * finished sentence ("Booked counts within 7 days"), which is what the card
 * renders. The number behind it is on `GET /{id}`, so the read is **gated on
 * the menu opening**: the window is a setting an agent changes rarely, and
 * every campaign detail page would otherwise pay for a second fetch nobody
 * looks at.
 *
 * Saving re-runs attribution server-side, so every campaign query is dropped —
 * the headline figures, the per-step results and the index row all move.
 */
export function useCampaignAttribution(
  campaignId: string,
  open: boolean,
): CampaignAttributionResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const query = useGetCampaignApiV1CampaignsCampaignIdGet(campaignId, {
    query: { enabled: open },
  })
  const current = query.data?.data.attribution_window_days ?? null

  const update = useUpdateCampaignApiV1CampaignsCampaignIdPatch({
    mutation: {
      onSuccess: () => {
        toast.success(t('campaigns.detail.attribution.saved'))
        invalidateCampaignQueries(queryClient)
      },
      onError: (error) =>
        toast.error(
          getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
        ),
    },
  })

  const onChange = useCallback(
    (days: number) => {
      if (days === current) return
      update.mutate({ campaignId, data: { attribution_window_days: days } })
    },
    [update, campaignId, current],
  )

  const options =
    current == null || ATTRIBUTION_WINDOWS.includes(current as 1)
      ? [...ATTRIBUTION_WINDOWS]
      : [...ATTRIBUTION_WINDOWS, current].sort((a, b) => a - b)

  return { current, options, onChange, isSaving: update.isPending }
}
